import { useId, type InputHTMLAttributes, type ReactNode } from 'react';
import './Field.css';

interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  /** Message d'erreur affiche sous le champ et annonce aux lecteurs d'ecran. */
  error?: string | undefined;
  /** Aide permanente affichee sous le champ. */
  hint?: ReactNode;
}

/**
 * Champ de formulaire accessible : etiquette liee au champ, message d'erreur
 * relie par `aria-describedby`, etat invalide expose par `aria-invalid`.
 */
export function Field({ label, error, hint, className, ...rest }: FieldProps) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ');

  return (
    <div className={`fg-field ${className ?? ''}`}>
      <label className="fg-field__label" htmlFor={id}>
        {label}
        {rest.required && (
          <span className="fg-field__required" aria-hidden="true">
            *
          </span>
        )}
      </label>

      <input
        id={id}
        className={`fg-field__input ${error ? 'fg-field__input--invalid' : ''}`}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        {...rest}
      />

      {hint && (
        <p className="fg-field__hint" id={hintId}>
          {hint}
        </p>
      )}

      {error && (
        <p className="fg-field__error" id={errorId} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
